import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Clock, Maximize2, Minimize2, Plus, Printer, X } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS, formatMoney, monthTotals } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { MonthTableRow, SORTS, TableHead, sortServices } from "@/components/app/MonthTable";
import { BulkBar } from "@/components/app/BulkBar";
import { Checkbox } from "@/components/ui/checkbox";

const usePref = (key, initial) => {
  const [v, setV] = useState(() => localStorage.getItem(key) ?? initial);
  const set = (next) => { localStorage.setItem(key, next); setV(next); };
  return [v, set];
};

const MonthChips = ({ month, year, services, onMonth }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector(`[data-month="${month}"]`)?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [month]);
  return (
    <div ref={ref} className="thin-scroll -mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1" data-testid="month-chips">
      {MONTHS.map((name, i) => {
        const prefix = `${year}-${String(i + 1).padStart(2, "0")}`;
        const pending = services.filter((s) => s.status === "agendado" && s.execution_date.startsWith(prefix)).length;
        const active = i === month;
        return (
          <button key={name} data-month={i} onClick={() => onMonth(i)} data-testid={`month-chip-${i + 1}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${active ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            {name.slice(0, 3)}{pending > 0 && <span className={`ml-1 font-mono ${active ? "text-rose-600" : "text-rose-300"}`}>{pending}</span>}
          </button>
        );
      })}
    </div>
  );
};

const Group = ({ title, icon: Icon, items, cls, testid, wide, openId, setOpenId, selected, toggleSel, setMany }) => {
  const all = items.length > 0 && items.every((s) => selected.has(s.id));
  return (
  <section data-testid={testid}>
    <h3 className={`mb-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold uppercase tracking-wider ${cls}`}>
      <Checkbox checked={all} onCheckedChange={(v) => setMany(items, !!v)} className="border-current" aria-label={`Selecionar todos os ${title.toLowerCase()}`} data-testid={`${testid}-select-all`} />
      <Icon className="h-4 w-4" /> {title} <span className="font-mono" data-testid={`${testid}-count`}>{items.length}</span>
      <span className="ml-auto font-mono normal-case tracking-normal" data-testid={`${testid}-total`}>{formatMoney(items.reduce((a, s) => a + (Number(s.price) || 0), 0))}</span>
    </h3>
    <TableHead wide={wide} />
    <ul className="rounded-xl border border-slate-700/60 bg-slate-900/40">
      {items.map((s) => <MonthTableRow key={s.id} s={s} wide={wide} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)}
        checked={selected.has(s.id)} onCheck={() => toggleSel(s.id)} />)}
    </ul>
  </section>
  );
};

const SortPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="shrink-0 text-xs text-slate-400">Ordenar por</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 bg-slate-900 sm:w-60" data-testid="month-sort-select"><SelectValue /></SelectTrigger>
      <SelectContent className="border-slate-700 bg-slate-900">
        {SORTS.map(([k, label]) => <SelectItem key={k} value={k} data-testid={`month-sort-${k}`}>{label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Revenue = ({ list }) => {
  const { rows, done, planned, missing } = monthTotals(list);
  if (!rows.length) return null;
  return (
    <section className="rounded-xl border border-slate-700/60 bg-slate-800/60 p-4" data-testid="month-revenue">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">Faturamento do mês</h3>
      <div className="mb-1 grid grid-cols-[1fr_auto_auto] gap-x-3 text-[11px] uppercase tracking-wider text-slate-500"><span>Serviço</span><span className="text-right">Realizado</span><span className="text-right">Previsto</span></div>
      {rows.map((r) => (
        <div key={r.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 py-1 text-sm" data-testid={`month-revenue-row-${r.key}`}>
          <span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: r.hex }} /><span className="truncate">{r.label}</span></span>
          <span className="text-right font-mono text-emerald-300">{formatMoney(r.done)}</span>
          <span className="text-right font-mono text-slate-400">{formatMoney(r.planned)}</span>
        </div>
      ))}
      <div className="mt-2 grid grid-cols-[1fr_auto_auto] gap-x-3 border-t border-slate-700/60 pt-2 text-sm font-bold">
        <span>Total do mês</span>
        <span className="text-right font-mono text-emerald-300" data-testid="month-revenue-done">{formatMoney(done)}</span>
        <span className="text-right font-mono text-slate-300" data-testid="month-revenue-planned">{formatMoney(planned)}</span>
      </div>
      <p className="mt-1 text-right text-xs text-slate-400" data-testid="month-revenue-total">Realizado + previsto: <b className="font-mono text-slate-100">{formatMoney(done + planned)}</b></p>
      {missing > 0 && <p className="mt-1 text-xs text-amber-300" data-testid="month-revenue-missing">{missing} serviço(s) sem valor. Preencha o valor na ficha do cliente (Serviços contratados) ou no lançamento.</p>}
    </section>
  );
};

export const MonthSheet = ({ month, year, services, onClose, onMonth }) => {
  const { openNew, today } = useServices();
  const [sort, setSort] = usePref("ciclo-month-sort", "data");
  const [wideMode, setWideMode] = usePref("ciclo-month-wide", "0");
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [month, year]);
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setMany = (items, on) => setSelected((prev) => { const n = new Set(prev); items.forEach((s) => (on ? n.add(s.id) : n.delete(s.id))); return n; });
  const wide = wideMode === "1";
  const open = month !== null;
  const mm = String((month ?? 0) + 1).padStart(2, "0");
  const prefix = `${year}-${mm}`;
  const list = open ? services.filter((s) => s.execution_date.startsWith(prefix)) : [];
  const pending = sortServices(list.filter((s) => s.status === "agendado"), sort);
  const done = sortServices(list.filter((s) => s.status === "realizado"), sort);
  const defaultDate = today.startsWith(prefix) ? today : `${prefix}-01`;
  const groupProps = { wide, openId, setOpenId, selected, toggleSel, setMany };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className={`thin-scroll w-full overflow-y-auto border-slate-700 bg-slate-900 p-0 transition-[max-width] duration-200 ${wide ? "sm:max-w-[min(1280px,96vw)]" : "sm:max-w-xl"}`} data-testid="month-sheet">
        <div className="sticky top-0 z-20 border-b border-slate-700/70 bg-slate-900/95 px-6 pb-3 pt-6 backdrop-blur-xl" data-testid="month-sheet-header">
          <button type="button" onClick={() => setWideMode(wide ? "0" : "1")} title={wide ? "Diminuir a aba" : "Aumentar a aba"} aria-label={wide ? "Diminuir a aba" : "Aumentar a aba"}
            className="absolute right-16 top-4 hidden h-9 items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800 px-3 text-xs font-semibold text-slate-200 transition-colors duration-150 hover:bg-slate-700 sm:flex" data-testid="month-sheet-expand">
            {wide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />} {wide ? "Diminuir" : "Aumentar"}
          </button>
          <SheetClose className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-slate-200 transition-colors duration-150 hover:bg-slate-700" aria-label="Fechar" data-testid="month-sheet-close">
            <X className="h-4 w-4" />
          </SheetClose>
          <SheetHeader className="pr-12 text-left sm:pr-36">
            <SheetTitle className="font-display text-3xl font-extrabold tracking-tight">{open ? `${MONTHS[month]} ${year}` : ""}</SheetTitle>
            <SheetDescription data-testid="month-sheet-summary">
              <span className="font-semibold text-rose-300">{pending.length} pendente(s)</span> · <span className="font-semibold text-emerald-300">{done.length} realizado(s)</span>
            </SheetDescription>
          </SheetHeader>
          {open && <MonthChips month={month} year={year} services={services} onMonth={onMonth} />}
        </div>
        <div className={`px-6 pb-8 pt-4 ${wide ? "lg:grid lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start lg:gap-6" : ""}`}>
          <div className={wide ? "lg:sticky lg:top-40" : ""}>
            <Button onClick={() => openNew({ execution_date: defaultDate })} className="w-full rounded-full font-semibold" data-testid="month-add-service-button">
              <Plus className="mr-1 h-4 w-4" /> Adicionar serviço neste mês
            </Button>
            <Button asChild variant="outline" className="mt-2 w-full rounded-full border-slate-700 bg-transparent" data-testid="month-report-button">
              <a href={`/relatorio/${year}/${(month ?? 0) + 1}`} target="_blank" rel="noreferrer"><Printer className="mr-1 h-4 w-4" /> Relatório para imprimir</a>
            </Button>
            <div className="mt-6"><Revenue list={list} /></div>
          </div>
          <div className={`space-y-6 ${wide ? "mt-6 lg:mt-0" : "mt-6"}`}>
            {list.length > 0 && <SortPicker value={sort} onChange={setSort} />}
            {list.length === 0 && <p className="py-10 text-center text-sm text-slate-500" data-testid="month-empty">Nenhum serviço lançado neste mês.</p>}
            {pending.length > 0 && <Group title="Pendentes" icon={Clock} items={pending} cls="bg-rose-500/20 text-rose-200" testid="month-pending" {...groupProps} />}
            {done.length > 0 && <Group title="Realizados" icon={CheckCircle2} items={done} cls="bg-emerald-500/20 text-emerald-200" testid="month-done" {...groupProps} />}
            <BulkBar selected={selected} list={list} onClear={() => setSelected(new Set())} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
